// Whiteboard — "Drawings on this work item": the work item's own PDFs and
// pictures, to put on the board and mark up — or a file from this device,
// attached to the work item first (so it stays with the work item) and then
// brought in the same way. The shop zebra that ran here
// waits on the card: it stands, then sits down like a dog, wagging its tail;
// picking a drawing makes it look up (and wag faster). Bringing one in (the
// server copies it; a PDF gets its first page rendered) focuses the board on it.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import whiteboardApi from '@modules/database/whiteboard/frontend/api';
import { Icon } from '@modules/database/whiteboard/frontend/ui';
import { ShopZebra } from '@modules/database/whiteboard/frontend/ZebraArt';

const thumbs = new Map();   // attachment id → Promise<object URL | null>
function useThumb(id) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let on = true;
    if (!thumbs.has(id)) thumbs.set(id, whiteboardApi.attachmentThumb(id).then((b) => (b && b.size ? URL.createObjectURL(b) : null)).catch(() => null));
    thumbs.get(id).then((u) => { if (on) setUrl(u); });
    return () => { on = false; };
  }, [id]);
  return url;
}

const size = (n) => (!n ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const day = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const extOf = (name) => (String(name).split('.').pop() || '').toUpperCase().slice(0, 4);

function Card({ a, selected, busy, onPick, onOpen }) {
  const url = useThumb(a.id);
  return (
    <button type="button" className={`wb-att ${selected ? 'is-selected' : ''}`} onClick={onPick} onDoubleClick={onOpen} aria-pressed={selected} disabled={busy}>
      <span className={`wb-att-thumb is-${a.kind}`}>
        {url ? <img src={url} alt="" /> : <Icon name={a.kind === 'pdf' ? 'pdf' : 'image'} size={34} />}
        <span className={`wb-att-badge is-${a.kind}`}>{a.kind === 'pdf' ? 'PDF' : extOf(a.name)}</span>
        {selected && busy && <span className="wb-att-busy"><span className="wb-spin" />Bringing it in…</span>}
      </span>
      <span className="wb-att-name">{a.name}</span>
      <span className="wb-att-meta">{[size(a.size), day(a.created), a.author].filter(Boolean).join(' · ')}</span>
    </button>
  );
}

export default function AttachmentPicker({ workItemKey, fieldId, onPlaced, onAttached, onClose }) {
  const [list, setList] = useState(null);
  const [attaching, setAttaching] = useState(null);   // { i, of, name } while files from this device go up
  const [err, setErr] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [find, setFind] = useState('');
  // the zebra: stands, then sits like a dog; looks up when a drawing is picked
  const [pose, setPose] = useState('standing');
  const [perk, setPerk] = useState(false);
  const perkTimer = useRef(null);
  useEffect(() => {
    const sit = setTimeout(() => setPose('sitting'), 1100);
    return () => { clearTimeout(sit); clearTimeout(perkTimer.current); };
  }, []);
  const perkUp = useCallback(() => {
    setPerk(true); clearTimeout(perkTimer.current);
    perkTimer.current = setTimeout(() => setPerk(false), 900);
  }, []);

  const [rev, setRev] = useState(0);
  useEffect(() => {
    let on = true;
    whiteboardApi.attachments(workItemKey, fieldId)
      .then((d) => { if (on) setList(d.attachments || []); })
      .catch((e) => { if (on) setErr(e?.response?.data?.error || e?.message || 'The attachments could not be read.'); });
    return () => { on = false; };
  }, [workItemKey, fieldId, rev]);

  // Attach from device: each file becomes an attachment of the work item, then
  // comes onto the board like any other (the last one opens in focus).
  async function attachLocal(files) {
    if (busy || !files.length) return;
    setBusy(true); setErr(null);
    const placed = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setAttaching({ i: i + 1, of: files.length, name: f.name });
        const out = await whiteboardApi.attach(workItemKey, f);
        const att = Array.isArray(out) ? out[0] : out;
        if (!att?.id) throw new Error(`${f.name} could not be attached.`);
        onAttached?.(att);
        const res = await whiteboardApi.fromAttachment(workItemKey, fieldId, att.id);
        placed.push([res, { id: att.id, name: att.filename || f.name }]);
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.error || e?.response?.data?.errorMessages?.[0] || e?.message;
      setErr(status === 403
        ? `You may not attach files to ${workItemKey}. Ask for the right to add attachments — or put the file on the board only with the rail's picture button.`
        : status === 413 ? 'That file is too large to attach.'
          : `${msg || 'The file could not be attached.'}${placed.length ? ' (The files before it are attached — pick them below.)' : ''}`);
      setAttaching(null); setBusy(false);
      setRev((r) => r + 1);   // what did get attached shows in the list
      return;
    }
    setAttaching(null);
    for (const [res, a] of placed) onPlaced(res, a);
  }

  const shown = useMemo(() => (list || []).filter((a) => !find.trim() || a.name.toLowerCase().includes(find.trim().toLowerCase())), [list, find]);

  async function bring(a) {
    if (!a || busy) return;
    setPick(a.id); setBusy(true); setErr(null);
    try {
      const res = await whiteboardApi.fromAttachment(workItemKey, fieldId, a.id);
      onPlaced(res, a);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'That file could not be brought onto the board.');
      setBusy(false);
    }
  }

  return (
    <div className="wb-modal-scrim" onPointerDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="wb-modal wb-picker" role="dialog" aria-modal="true" aria-label="Drawings on this work item"
        onKeyDown={(e) => { if (e.key === 'Escape' && !busy) { e.stopPropagation(); onClose(); } }}>
        <div className="wb-picker-zebra" aria-hidden="true"><ShopZebra width={110} pose={busy ? 'standing' : pose} perk={perk} /></div>
        <div className="wb-modal-head wb-picker-head">
          <div>
            <div className="wb-modal-title">Drawings on {workItemKey}</div>
            <div className="wb-modal-sub">Pick a PDF or a picture attached to this work item, or attach one from your device — it opens in focus, ready to mark up.</div>
          </div>
          <button type="button" className="wb-btn" aria-label="Close" onClick={onClose} disabled={busy}><Icon name="close" /></button>
        </div>
        {list && list.length > 6 && (
          <input className="wb-input wb-picker-find" placeholder="Find a file by name" value={find} onChange={(e) => setFind(e.target.value)} aria-label="Find a file by name" />
        )}
        <div className="wb-picker-body">
          {!list && !err && <div className="wb-picker-empty"><span className="wb-spin" />Fetching the work item's drawings…</div>}
          {attaching && <div className="wb-picker-empty"><span className="wb-spin" />Attaching {attaching.of > 1 ? `${attaching.i} of ${attaching.of} · ` : ''}{attaching.name} to {workItemKey}…</div>}
          {list && !list.length && !attaching && (
            <div className="wb-picker-empty">
              <Icon name="file" size={26} />
              <div><b>No PDFs or pictures are attached to {workItemKey} yet.</b><br />Attach one from your device — it is added to the work item and opens here.</div>
            </div>
          )}
          {list && list.length > 0 && (
            <div className="wb-att-grid">
              {shown.map((a) => (
                <Card key={a.id} a={a} selected={pick === a.id} busy={busy} onPick={() => { setPick(a.id); perkUp(); }} onOpen={() => bring(a)} />
              ))}
              {!shown.length && <div className="wb-picker-empty">No file matches “{find}”.</div>}
            </div>
          )}
          {err && <div className="wb-form-err">{err}</div>}
        </div>
        <div className="wb-modal-foot wb-picker-foot">
          <label className={`wb-text-btn wb-local-btn ${busy ? 'is-busy' : ''}`}>
            <Icon name="upload" size={16} />Attach from device
            <input type="file" accept="application/pdf,image/*" multiple hidden disabled={busy}
              onChange={(e) => { const f = [...(e.target.files || [])]; e.target.value = ''; if (f.length) attachLocal(f); }} />
          </label>
          <span style={{ flex: 1 }} />
          <button type="button" className="wb-text-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="wb-primary" disabled={!pick || busy} onClick={() => bring((list || []).find((a) => a.id === pick))}>
            <Icon name="focus" size={16} />{attaching ? 'Attaching…' : busy ? 'Bringing it in…' : 'Mark it up'}
          </button>
        </div>
      </div>
    </div>
  );
}
